
import React from 'react';
import { Music, ThumbsUp, Download, BarChart3, Lock, Unlock, Link, Gift } from 'lucide-react';

const FeatureSection: React.FC = () => {
  return (
    <div className="py-24 bg-secondary/50">
      <div className="container px-4 mx-auto sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
            How it works
          </h2>
          <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
            A simple three-step process to boost your Spotify presence and reward your fans
          </p>
        </div>

        <div className="relative">
          {/* Connection line */}
          <div className="absolute top-24 left-1/2 w-px h-[calc(100%-8rem)] bg-border hidden lg:block"></div>
          
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-3 relative">
            {/* Step 1 */}
            <div className="glass-card rounded-lg p-8 relative animate-fade-in [animation-delay:100ms] opacity-0">
              <div className="flex items-center justify-center w-12 h-12 mb-6 rounded-full bg-primary text-white">
                <Link size={20} />
              </div>
              <h3 className="text-xl font-semibold mb-3">Connect Your Track</h3>
              <p className="text-muted-foreground mb-4">
                Connect your Spotify track and upload the exclusive content you want to share.
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start">
                  <span className="mr-2 text-primary">✓</span>
                  <span>Upload MP3s, WAVs, MP4s, or any file type</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2 text-primary">✓</span>
                  <span>Connect existing Spotify tracks</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2 text-primary">✓</span>
                  <span>Track activity and downloads</span>
                </li>
              </ul>
            </div>

            {/* Step 2 */}
            <div className="glass-card rounded-lg p-8 animate-fade-in [animation-delay:200ms] opacity-0">
              <div className="flex items-center justify-center w-12 h-12 mb-6 rounded-full bg-primary text-white">
                <ThumbsUp size={20} />
              </div>
              <h3 className="text-xl font-semibold mb-3">Fans like your track</h3>
              <p className="text-muted-foreground mb-4">
                Share your link with fans—when they like your track on Spotify, they'll download your content.
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start">
                  <span className="mr-2 text-primary">✓</span>
                  <span>Reward Fans</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2 text-primary">✓</span>
                  <span>Grow your Spotify Engagement</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2 text-primary">✓</span>
                  <span>Boost Monthly Listeners</span>
                </li>
              </ul>
            </div>

            {/* Step 3 */}
            <div className="glass-card rounded-lg p-8 animate-fade-in [animation-delay:300ms] opacity-0">
              <div className="flex items-center justify-center w-12 h-12 mb-6 rounded-full bg-primary text-white">
                <Gift size={20} />
              </div>
              <h3 className="text-xl font-semibold mb-3">Unlock Rewards</h3>
              <p className="text-muted-foreground mb-4">
                After liking your track, fans can download your exclusive content instantly.
              </p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start">
                  <span className="mr-2 text-primary">✓</span>
                  <span>Instant download access</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2 text-primary">✓</span>
                  <span>High-quality file downloads</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2 text-primary">✓</span>
                  <span>Create loyal fan connections</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Dropify Banner - Now placed above "Benefits for Artists" */}
        <div className="mt-12 mb-12 flex justify-center">
          <img 
            src="/lovable-uploads/3b5ca2eb-6aa1-44ac-8237-23950c290b1d.png" 
            alt="Drop Tracks. Get Fans." 
            className="w-[95%] md:w-1/2 h-auto object-cover rounded-[6px] md:rounded-xl"
          />
        </div>

        <div className="text-center">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-12">
            Benefits for Artists
          </h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="p-6 rounded-lg text-center animate-fade-in [animation-delay:100ms] opacity-0">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary mx-auto mb-4">
                <BarChart3 size={24} />
              </div>
              <h3 className="text-lg font-medium mb-2">Increase Engagement</h3>
              <p className="text-sm text-muted-foreground">
                Boost your Spotify algorithm presence with more likes on your tracks.
              </p>
            </div>
            
            <div className="p-6 rounded-lg text-center animate-fade-in [animation-delay:200ms] opacity-0">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary mx-auto mb-4">
                <Lock size={24} />
              </div>
              <h3 className="text-lg font-medium mb-2">Exclusive Content</h3>
              <p className="text-sm text-muted-foreground">
                Share exclusive content directly with your most engaged fans.
              </p>
            </div>
            
            <div className="p-6 rounded-lg text-center animate-fade-in [animation-delay:300ms] opacity-0">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary mx-auto mb-4">
                <Unlock size={24} />
              </div>
              <h3 className="text-lg font-medium mb-2">Fan Rewards</h3>
              <p className="text-sm text-muted-foreground">
                Give back to your audience with valuable downloads they'll love.
              </p>
            </div>
            
            <div className="p-6 rounded-lg text-center animate-fade-in [animation-delay:400ms] opacity-0">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary mx-auto mb-4">
                <Music size={24} />
              </div>
              <h3 className="text-lg font-medium mb-2">Grow Your Audience</h3>
              <p className="text-sm text-muted-foreground">
                Build a stronger connection with your fans and expand your reach.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeatureSection;
